import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, List, ListItem, ListItemText, Typography } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import StatusChip from '../Common/StatusChip';
import demosApi from '../../utils/demos.api';

const PendingAccessPage = () => {
  const [requestData, setRequestData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    Promise.all([demosApi.getMyRequest(), demosApi.getCatalog()])
      .then(([reqRes, catRes]) => {
        // getMyRequest: wrapped {success, data: {request_id, requested_demo_ids:[int], ...}}
        const req = reqRes.data?.data ?? null;
        setRequestData(req);

        // getCatalog: plain [{app, demos:[{id, title, ...}]}]
        const apps = Array.isArray(catRes.data) ? catRes.data : [];
        const flat = apps.flatMap((g) => g.demos.map((d) => ({ ...d, app: g.app })));
        setCatalog(flat);
      })
      .catch(() => {
        setRequestData(null);
        setCatalog([]);
      })
      .finally(() => setLoading(false));
  }, [reloadTick]);

  // Resolve integer IDs to demo titles using the catalog
  const requestedDemos = (requestData?.requested_demo_ids ?? []).map((id) => {
    const found = catalog.find((d) => d.id === id);
    return found
      ? { id, title: found.title, app: found.app }
      : { id, title: `Demo #${id}`, app: '' };
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 400,
        gap: 3,
        p: 4,
        textAlign: 'center'
      }}
    >
      <HourglassEmptyIcon sx={{ fontSize: 72, color: 'warning.main', opacity: 0.8 }} />

      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Access Request Submitted
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420 }}>
          Your request has been sent to our team. You'll receive an email once access is granted.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">Status:</Typography>
        <StatusChip status={requestData?.status ?? 'pending'} />
      </Box>

      <Button
        variant="outlined"
        size="small"
        disabled={loading}
        onClick={() => setReloadTick((t) => t + 1)}
      >
        {loading ? 'Checking...' : 'Refresh Status'}
      </Button>

      {loading && <CircularProgress size={24} />}

      {!loading && requestedDemos.length > 0 && (
        <Box sx={{ textAlign: 'left', width: '100%', maxWidth: 400 }}>
          <Typography variant="subtitle2" gutterBottom>
            Requested demos:
          </Typography>
          <List dense disablePadding>
            {requestedDemos.map((demo) => (
              <ListItem key={demo.id} sx={{ py: 0.25, pl: 0 }}>
                <ListItemText
                  primary={`• ${demo.title}${demo.app ? ` (${demo.app})` : ''}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        Contact support if your request hasn't been reviewed within 24 hours.
      </Typography>
    </Box>
  );
};

export default PendingAccessPage;
